<template>
  <div class="fts-form-designer fts-element-designer">
    <!-- design / preview mode bar (§3.2b ledger text — Preview renders the real controls) -->
    <div class="form-designer-modebar" v-if="!document">
      <!-- preview test data: blank scratch entry, or any real entry of the module —
           searchable, labelled by the entry's document id + title -->
      <div class="form-designer-preview-pick" v-if="previewMode">
        <span class="form-designer-preview-pick-label">test with</span>
        <fts-select
          class="form-designer-preview-pick-select"
          :options="previewEntryOptions"
          :clearable="false"
          :disabled="previewLoading"
          placeholder="Blank entry"
          v-model="previewEntryModel"
        />
      </div>
      <div class="form-designer-modebar-states">
        <span
          class="form-designer-modebar-state"
          :class="{ 'form-designer-modebar-state--on': !previewMode }"
          @click="setPreviewMode(false)"
        >Design</span>
        <span class="form-designer-modebar-sep">·</span>
        <span
          class="form-designer-modebar-state"
          :class="{ 'form-designer-modebar-state--on': previewMode }"
          @click="setPreviewMode(true)"
        >Preview</span>
      </div>
    </div>
    <!-- live-rehearsal accounting: fields the rules are currently hiding -->
    <div class="form-designer-hiddenline" v-if="previewMode && previewHiddenCount">
      {{ previewHiddenCount }} field{{ previewHiddenCount > 1 ? "s" : "" }} hidden by rules
    </div>
    <!-- element design content -->
    <div class="form-designer-content element-designer-content fts-form-group">
      <fts-form-layouter
        :modules="modules"
        :form="previewMode && previewForm ? previewForm : form"
        :activity-id="activityId"
        :activity-design="previewMode && previewForm ? previewForm : form"
        :document="document"
        :information-source="information"
        :shortcut-options="shortcutOptions"
        :shortcut-elements="shortcutElements"
        :shortcut-selected-options="classificationInformation"
        :item-group="'row'"
        :preview="previewMode"
        :preview-data="previewData"
        @added="elementAdded"
      />
    </div>

    <!-- form health (ruling 2026-08-10): the count of formulas the save-time lint will
         reject, surfaced before Save instead of at it. Rows carry the per-line ✕ marks. -->
    <div class="form-designer-health" v-if="!document && !previewMode && formulaProblemCount">
      <span class="form-designer-health-mark">✕</span>
      {{ formulaProblemCount }} formula problem{{ formulaProblemCount > 1 ? "s" : "" }}
      — Save will reject {{ formulaProblemCount > 1 ? "them" : "it" }}
    </div>

    <div class="form-designer-footer" v-if="!document && !previewMode">
      <div class="design-modal-form-group" v-if="!disabledDelete">
        <fts-btn color="danger" sm outline text-transform-init @click="deleteForm">Remove Form</fts-btn>
      </div>

    </div>
  </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import draggable from "vuedraggable";
import VueJsonPretty from "vue-json-pretty";
import wysiwyg from "../vue-wysiwyg/src/editor/index";
Vue.use(wysiwyg, {});
import pdf from "vue-pdf/src/vuePdfNoSss";

import { authorizationStore } from "../store/authorizationStore";
import { automationDesignStore } from "../store/automationDesignStore";
import { diagnose } from "../design/formula/formulaDiagnostics";
import { loadFormulaFunctions } from "../design/formula/formulaFunctions";
import { buildNativeModel, nativeRecompute } from "../store/nativeCompute";
import { NATIVE_ENGINE_VERSION } from "../../business/nativeFormula";
import commons from "../commons/commons";
import formDesign from '../commons/formDesign';

import FTSDesignElement from "./FTSDesignElement";
import FTSDesignToolItem from "./FTSDesignToolItem";
import FTSForm from "./FTSForm";
import FTSFormLayouter from "../design/FTSFormLayouter";
import FTSBtn from "./FTSBtn";
import FTSSelect from "./FTSSelect";
import FTSTippy from "../elements/FTSTippy";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";

export default {
  components: {
    draggable,
    VueJsonPretty,
    pdf,
    "fts-design-element": FTSDesignElement,
    "fts-design-tool-item": FTSDesignToolItem,
    "fts-form": FTSForm,
    "fts-btn": FTSBtn,
    "fts-select": FTSSelect,
    "fts-tippy": FTSTippy,
    "fts-toggle-switch": FTSToggleSwitch
  },
  props: {
    modules: Array,
    document: Boolean,
    activityId: String,
    form: {
      type: Object,
      default: () => ({})
    },
    // the canvas ACTIVITY node (id/name/design/...) — carries the per-activity
    // serverNative formula opt-in; the toggle renders only when the host passes it
    activity: Object,
    isCapableAddInformation: {
      type: Boolean,
      default: true
    },
    disabledDelete: Boolean
  },
  data: () => ({
    /* Preview renders the actual interactive form onto this shared scratch entry — reset on
       every entry into preview so each rehearsal starts blank. The designer can also load any
       real entry of the module as test data (read via the view form, values only). */
    previewMode: false,
    previewData: {},
    previewEntries: null,
    previewEntryId: null,
    previewLoading: false,
    /* live engine (mockup B): Preview renders a CLONE of the design so the on-device control
       formulas (visible/required/readOnly) apply to preview elements, never the working copy.
       The native model comes from this activity's SAVED bundle — same "as of the last save"
       contract as the sync chips and the server-native verdict. */
    previewForm: null,
    previewNativeModel: null,
    fnRegistry: null,
    /* per-field sync indicator (design-time twin of the runtime's trigger model): the whole-form
       verdict from api/canvastest/synctriggers, refreshed for ALL fields whenever any field
       changes. Provided (as this stable reactive object) to every FTSDesignElement below. */
    syncIndicator: { verdict: null },
    syncTriggerSeq: 0
  }),
  provide() {
    return { formSyncIndicator: this.syncIndicator };
  },
  computed: {
    moduleId() {
      return this.$store.state.vectorId.toString();
    },
    previewEntryOptions() {
      const entries = (this.previewEntries || []).map(e => ({
        id: e.id,
        name: e.documentId ? `${e.documentId} — ${e.title}` : String(e.title)
      }));
      return [{ id: null, name: "Blank entry" }, ...entries];
    },
    previewEntryModel: {
      get() {
        return this.previewEntryId;
      },
      set(val) {
        this.previewEntryId = val || null;
        this.onPreviewEntryChange();
      }
    },
    beta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    classificationInformation() {
      return commons.classificationInformation(
        this.form.rows,
        this.information
      );
    },
    shortcutOptions() {
      return [...this.information].filter(
        x => !this.classificationInformation.some(y => y.id === x.id)
      );
    },
    prefix() {
      if (this.document) {
        return [
          formDesign.Element.Content,
          formDesign.Element.Image,
          formDesign.Element.Space,
          {
            _name: "Section",
            input: "section",
            type: "section",
            design: { rows: [] }
          },
          {
            _name: "Column",
            input: "section",
            type: "column",
            design: { rows: [] }
          }
        ];
      }
      return [
        ...formDesign.elements,
        {
          _name: "Section",
          input: "section",
          type: "section",
          design: { rows: [] }
        },
        {
          _name: "Collapse",
          input: "section",
          type: "collapse",
          design: { rows: [] }
        },
        {
          _name: "Tab",
          input: "section",
          type: "tab",
          design: { rows: [] },
          tabs: [
            {
              id: commons.nanoID(),
              header: "(new tab)",
              design: { rows: [] }
            }
          ]
        },
        {
          _name: "Column",
          input: "section",
          type: "column",
          design: { rows: [] }
        }
      ];
    },
    shortcutElements() {
      return [...this.prefix.filter(x => (this.beta && x.beta) || !x.beta)];
    },
    /* preview accounting: fields the control rules (or static config) currently hide */
    previewHiddenCount() {
      if (!this.previewMode || !this.previewForm) return 0;
      let count = 0;
      const walkRows = (rows) => {
        (rows || []).forEach(row => (row.items || []).forEach(item => {
          if (item.visible === false) count++;
          if (item.design && item.design.rows) walkRows(item.design.rows);
          (item.tabs || []).forEach(tab => tab.design && walkRows(tab.design.rows));
        }));
      };
      walkRows(this.previewForm.rows);
      return count;
    },
    /* form health: how many of this form's formulas the save-time lint would reject —
       walks the working design (fields, table columns, sections, tabs, nested lists) with
       the same diagnose the editors run live */
    formulaProblemCount() {
      if (!this.fnRegistry || !this.form || this.document) return 0;
      let count = 0;
      const check = (text) => {
        if (text && diagnose(text, this.fnRegistry, { excelSyntax: true })
          .some(d => d.severity === "error")) count++;
      };
      const checkElement = (el) => {
        check(el.formula);
        check(el.requiredFormula);
        check(el.readOnlyFormula);
        check(el.visibleFormula);
      };
      const walkRows = (rows) => {
        (rows || []).forEach(row => (row.items || []).forEach(item => {
          checkElement(item);
          (item.fields || []).forEach(checkElement);
          if (item.design && item.design.rows) walkRows(item.design.rows);
          (item.tabs || []).forEach(tab => tab.design && walkRows(tab.design.rows));
        }));
      };
      walkRows(this.form.rows);
      return count;
    },
  },
  methods: {
    setPreviewMode(on) {
      if (this.previewMode === Boolean(on)) return;
      this.previewMode = Boolean(on);
      if (on) {
        this.previewData = {};
        this.previewEntryId = null;
        this.previewForm = _.cloneDeep(this.form);
        if (this.previewEntries === null) this.loadPreviewEntries();
        if (this.previewNativeModel === null) this.loadPreviewBundle();
        else this.recomputePreview();
      } else {
        this.previewForm = null;
      }
    },
    /* the activity's own saved NativeFormulaBundle — powers the on-device rehearsal.
       The bundle only ships when the request carries the D-7 engine-version handshake, and
       the combined standard activity's designer id maps to its runtime lens. */
    async loadPreviewBundle() {
      if (!this.moduleId || !this.activityId) return;
      const runtimeActivityId = this.activityId === "createEditView" ? "create" : this.activityId;
      try {
        const res = await this.$store.state.client.post("/api/activity/form", {
          moduleId: this.moduleId,
          listingId: "everything",
          activityId: runtimeActivityId,
          nativeEngineVersion: NATIVE_ENGINE_VERSION
        });
        const bundle = res.data && res.data.classificationForm && res.data.classificationForm.nativeBundle;
        this.previewNativeModel = buildNativeModel(bundle) || false;
        this.recomputePreview();
      } catch (err) {
        this.previewNativeModel = false; // rehearsal stays static
      }
    },
    /* run the SAME evaluator the runtime uses over the preview clone: computed values write
       into previewData, control formulas apply to the cloned elements */
    recomputePreview() {
      if (!this.previewMode || !this.previewNativeModel || !this.previewForm) return;
      const context = {
        state: {
          nativeModel: this.previewNativeModel,
          formData: this.previewData,
          form: this.previewForm
        }
      };
      try {
        nativeRecompute(context);
      } catch (err) {
        return; // a broken working design must never crash the rehearsal
      }
      if (context.state.formData !== this.previewData) {
        this.previewData = context.state.formData;
      }
    },
    /* sample list for the preview picker — same source and shape-tolerance as the htmljs
       document sample picker (docEntries → /api/workspace/list) */
    async loadPreviewEntries() {
      if (!this.moduleId) return;
      try {
        const res = await this.$store.dispatch("docEntries", {
          moduleId: this.moduleId,
          listingId: "everything",
          pageSize: 50
        });
        const d = res && res.data;
        const list =
          (d && d.data && d.data.list) ||
          (d && d.list) ||
          (Array.isArray(d && d.data) ? d.data : null) ||
          (Array.isArray(d) ? d : []) ||
          [];
        this.previewEntries = list.map(x => ({
          id: x.id,
          title: x.title || x.name || x.id,
          documentId: x.documentId
        }));
      } catch (err) {
        console.error(err);
        this.previewEntries = [];
      }
    },
    /* load the chosen entry's values through the view form — classificationForm.default is the
       exact formData shape the runtime binds, so the preview controls read it natively */
    async onPreviewEntryChange() {
      if (!this.previewEntryId) {
        this.previewData = {};
        return;
      }
      this.previewLoading = true;
      try {
        const res = await this.$store.state.client.post("/api/activity/form", {
          moduleId: this.moduleId,
          listingId: "everything",
          activityId: "view",
          entryId: this.previewEntryId,
          // the legacy form endpoint reads the entry from locusId; entryId serves the x-form path
          locusId: this.previewEntryId
        });
        this.previewData =
          (res.data && res.data.classificationForm && res.data.classificationForm.default) || {};
      } catch (err) {
        console.error(err);
        this.$toasted.global.error();
        this.previewEntryId = null;
        this.previewData = {};
      } finally {
        this.previewLoading = false;
      }
    },
    /* Per-field sync indicator: classify the WORKING (unsaved) design so the chips answer for
       what is on screen. Sequenced so a stale (slower) response can never overwrite a newer
       verdict; failures keep the previous indicators (a mid-edit design can be transiently
       unresolvable). */
    async refreshSyncTriggers() {
      if (!this.moduleId || !this.activityId || this.document) return;
      const seq = ++this.syncTriggerSeq;
      try {
        const result = await this.$store.dispatch("classifySyncTriggers", {
          activityId: this.activityId,
          design: this.form,
          moduleInformation: this.information
        });
        if (seq !== this.syncTriggerSeq) return;
        if (result.data && !result.data.error) {
          this.$set(this.syncIndicator, "verdict", result.data);
          // D-8: the automation designer's Start node and the declaration rows (logic action /
          // backendScript) render the effective combined trigger set from this same verdict.
          automationDesignStore.commit("setSyncVerdict", result.data);
        }
      } catch (err) {
        /* keep previous indicators */
      }
    },
    // one debounced entry point for every change source (field edits, formula edits, script/
    // automation toggles, information changes) — refreshes ALL field chips at once
    debouncedSyncRefresh: _.debounce(function() {
      this.refreshSyncTriggers();
    }, 800),
    debouncedPreviewRecompute: _.debounce(function() {
      this.recomputePreview();
    }, 150),
    deleteForm() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: "Remove form?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Remove",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$emit("form:delete");
            }
          }
        ]
      });
    },
    elementAdded(element) {
      if (element.custom) {
        if (!element.id) element.id = commons.nanoID();
      } else if (
        !element.id &&
        this.prefix.some(x => x.input === element.input)
      ) {
        this.$set(element, "id", commons.nanoID());
      }
    }
  },
  beforeCreate: function() {
    Vue.component("fts-form-layouter", FTSFormLayouter);
    Vue.component("fts-design-element", FTSDesignElement);
    Vue.component("fts-form", FTSForm);
  },
  // watch: {
  //   formAutomation: function(val) {
  //     if (val) {
  //       this.$refs.automationDesigner.loadDiagram();
  //     }
  //   }
  // },
  watch: {
    /* any field change anywhere in the working design (values, formulas, columns, backendScript,
       automation) re-classifies the WHOLE form's sync chips */
    form: {
      deep: true,
      handler() {
        this.debouncedSyncRefresh();
      }
    },
    /* live rehearsal: every preview edit re-runs the native evaluator. The recompute replaces
       previewData only when something changed, so the follow-up firing settles immediately. */
    previewData: {
      deep: true,
      handler() {
        if (this.previewMode) this.debouncedPreviewRecompute();
      }
    },
    // module schema edits (new/renamed fields) shift trigger resolution too
    information: {
      deep: true,
      handler() {
        this.debouncedSyncRefresh();
      }
    }
  },
  mounted() {
    this.refreshSyncTriggers();
    loadFormulaFunctions(action => this.$store.dispatch(action))
      .then(list => { this.fnRegistry = list; })
      .catch(() => { /* the health line simply stays off */ });

    /* Automation graph edits are invisible to the `form` watcher: drawflow adds/removes block
       keys by plain assignment/delete on a non-reactive map, so node add, node delete and
       connection changes never re-classify. The verdict then keeps citing block keys from an
       older graph — a block the designer has since deleted — while the corner badges show the
       live keys. Ride the same signal the badges do. */
    window.eventBus.$on("refreshAutomationBlocks", this.debouncedSyncRefresh);
  },
  beforeDestroy() {
    window.eventBus.$off("refreshAutomationBlocks", this.debouncedSyncRefresh);
  }
};
</script>
<style lang="scss">
@import "~vue-wysiwyg/dist/vueWysiwyg.css";

.fts-form-designer {
  .form-designer-footer {
    padding-top: 1.5rem;
    border-top: 1px solid var(--fts-border);
  }
  .automation-design-content {
    margin-top: 1rem;
  }
}
</style>

